import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import LoginForm from './components/LoginForm';
import SocialLogin from './components/SocialLogin';
import PhoneLogin from './components/PhoneLogin';
import TrustSignals from './components/TrustSignals';

const UserLogin = () => {
  const [loginMethod, setLoginMethod] = useState('email');

  return (
    <>
      <Helmet>
        <title>Login - JobPortal India | Access Your Account</title>
        <meta name="description" content="Sign in to your JobPortal India account to access personalized job recommendations, track applications, and manage your career journey." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
          <div className="hidden lg:block">
            <div className="space-y-6">
              <Link to="/" className="inline-flex items-center gap-3 group">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center transition-smooth group-hover:scale-110">
                  <Icon name="Briefcase" size={28} color="white" />
                </div>
                <span className="text-2xl font-bold text-foreground">JobPortal India</span>
              </Link>

              <div className="space-y-4">
                <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                  Welcome Back to Your Career Journey
                </h1>
                <p className="text-lg text-muted-foreground">
                  Access thousands of job opportunities across India. Sign in to continue your job search and manage applications.
                </p>
              </div>

              <div className="relative rounded-2xl overflow-hidden shadow-modal">
                <Image
                  src="https://images.unsplash.com/photo-1719978184147-c5bf6b82c6e1"
                  alt="Diverse group of Indian professionals collaborating in modern office space with laptops and documents on conference table"
                  className="w-full h-80 object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {[
                { icon: 'Users', label: '50,000+ Active Users', color: 'var(--color-primary)' },
                { icon: 'Briefcase', label: '10,000+ Jobs Posted', color: 'var(--color-accent)' },
                { icon: 'TrendingUp', label: '85% Success Rate', color: 'var(--color-success)' }]?.
                map((stat, index) =>
                <div key={index} className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Icon name={stat?.icon} size={20} color={stat?.color} />
                    </div>
                    <p className="text-xs font-medium text-foreground">{stat?.label}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full">
            <div className="bg-card border border-border rounded-2xl shadow-modal p-6 sm:p-8">
              <div className="lg:hidden mb-6">
                <Link to="/" className="inline-flex items-center gap-2">
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Briefcase" size={24} color="white" />
                  </div>
                  <span className="text-xl font-bold text-foreground">JobPortal India</span>
                </Link>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Sign In</h2>
                <p className="text-muted-foreground">Enter your credentials to access your account</p>
              </div>

              <div className="flex gap-2 p-1 bg-muted rounded-lg mb-6">
                <button
                  onClick={() => setLoginMethod('email')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-smooth ${
                  loginMethod === 'email' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
                  }>

                  <Icon name="Mail" size={18} />
                  <span>Email</span>
                </button>
                <button
                  onClick={() => setLoginMethod('phone')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-smooth ${
                  loginMethod === 'phone' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
                  }>

                  <Icon name="Smartphone" size={18} />
                  <span>Phone</span>
                </button>
              </div>

              {loginMethod === 'email' ?
              <>
                  <LoginForm />
                  <div className="mt-6">
                    <SocialLogin />
                  </div>
                </> :

              <PhoneLogin onSwitchToEmail={() => setLoginMethod('email')} />
              }

              <div className="mt-8 text-center">
                <p className="text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <Link to="/user-registration" className="font-semibold text-primary hover:text-primary/80 transition-smooth">
                    Create Account
                  </Link>
                </p>
              </div>

              <TrustSignals />
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground">
                By signing in, you agree to our{' '}
                <button className="text-primary hover:text-primary/80 transition-smooth">Terms of Service</button>
                {' '}and{' '}
                <button className="text-primary hover:text-primary/80 transition-smooth">Privacy Policy</button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default UserLogin;